import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getContentSectionsList } from "../../../services/contentSectionsService";
import {
  getSectionItemsBySection,
  bulkAddSectionItems,
  reorderSectionItems,
  deleteSectionItem,
} from "../../../services/contentSectionItemsService";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import SectionsPanel from "./SectionsPanel";
import MappedContentsPanel from "./MappedContentsPanel";
import AddContentModal from "./AddContentModal";
import {
  getSectionId,
  normalizeMappedItemsList,
  buildReorderPayload,
} from "./sectionMappingUtils";

export const SectionMappingEngine = () => {
  const navigate = useNavigate();
  const pageLevelAccessUrl = "section-mapping";
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [pageAccessDetails, setPageAccessDetails] = useState({});

  const [sections, setSections] = useState([]);
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [selectedSection, setSelectedSection] = useState(null);
  const [mappedItems, setMappedItems] = useState([]);
  const [mappedLoading, setMappedLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchSections = useCallback(async () => {
    setSectionsLoading(true);
    try {
      const response = await getContentSectionsList();
      const list = response.data?.result || response.data || [];
      setSections(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setSectionsLoading(false);
    }
  }, []);

  const fetchMappedItems = useCallback(async (sectionId) => {
    if (!sectionId) return;
    setMappedLoading(true);
    try {
      const data = await getSectionItemsBySection(sectionId);
      setMappedItems(normalizeMappedItemsList(data));
    } catch (error) {
      handleErrors(error);
      setMappedItems([]);
    } finally {
      setMappedLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSections();
  }, [fetchSections]);

  const handleSelectSection = (section) => {
    setSelectedSection(section);
    const sectionId = getSectionId(section);
    fetchMappedItems(sectionId);
  };

  const handleReorder = async (reorderedItems) => {
    if (!pageAccessDetails.editAccess) return;

    const previousItems = mappedItems;
    setMappedItems(reorderedItems);
    setReordering(true);

    try {
      const payload = buildReorderPayload(reorderedItems);
      const response = await reorderSectionItems(payload);

      if (response?.data?.isSuccess === false) {
        setMappedItems(previousItems);
        toast.error(
          response.data.message || "Failed to save order. Please try again."
        );
        return;
      }

      toast.success("Homepage order updated.");
      await fetchMappedItems(getSectionId(selectedSection));
    } catch (error) {
      setMappedItems(previousItems);
      handleErrors(error);
    } finally {
      setReordering(false);
    }
  };

  const handleRemove = async (mappingItemId) => {
    if (!pageAccessDetails.deleteAccess) return;

    const confirmed = await confirmDelete("mapped content");
    if (!confirmed) return;

    try {
      await deleteSectionItem(mappingItemId);
      toast.success("Content removed from section.");
      await fetchMappedItems(getSectionId(selectedSection));
    } catch (error) {
      handleErrors(error);
    }
  };

  const handleBulkAdd = async (contentIds) => {
    if (!pageAccessDetails.addAccess || !selectedSection) return;

    const sectionId = getSectionId(selectedSection);
    setBulkSubmitting(true);

    try {
      const response = await bulkAddSectionItems({
        contentSectionId: Number(sectionId),
        contentIds,
      });

      if (response?.data?.isSuccess === false) {
        toast.error(
          response.data.message || "Failed to add contents. Please try again."
        );
        return;
      }

      toast.success("Contents added to section successfully.");
      setShowAddModal(false);
      await fetchMappedItems(sectionId);
    } catch (error) {
      handleErrors(error);
    } finally {
      setBulkSubmitting(false);
    }
  };

  return (
    <>
      <ComponentHeader title="Homepage Section Mapping" />

      <div className="row g-3">
        <div className="col-lg-4 col-xl-3">
          <SectionsPanel
            sections={sections}
            selectedSectionId={
              selectedSection ? getSectionId(selectedSection) : null
            }
            loading={sectionsLoading}
            onSelectSection={handleSelectSection}
          />
        </div>
        <div className="col-lg-8 col-xl-9">
          <MappedContentsPanel
            selectedSection={selectedSection}
            mappedItems={mappedItems}
            loading={mappedLoading}
            reordering={reordering}
            onAddContent={() => setShowAddModal(true)}
            onRemove={handleRemove}
            onReorder={handleReorder}
            canAdd={Boolean(pageAccessDetails.addAccess)}
            canDelete={Boolean(pageAccessDetails.deleteAccess)}
            canReorder={Boolean(pageAccessDetails.editAccess)}
          />
        </div>
      </div>

      <AddContentModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        mappedItems={mappedItems}
        onBulkAdd={handleBulkAdd}
        isSubmitting={bulkSubmitting}
        canAdd={Boolean(pageAccessDetails.addAccess)}
      />
    </>
  );
};
